import _ from 'lodash'
import Backbone from 'backbone'
import template from './choose-columns.hbs'
import FormView from 'scoreboard/components/form-view'
import i18n from 'scoreboard/components/i18n'
import chooseColumnsRow from './choose-columns-row.hbs'
import CovertButtonTooltips from 'scoreboard/components/covert-button-tooltips'
import Dataset, { isEditField } from '../model/dataset'
const { TOTAL } = Dataset
import aggregationLabel from './aggregation-label'
import ColumnFormDialog from '../column-form-dialog'
import appDisplay from 'scoreboard/components/app-display'
import DraggableList from 'draggable-list'
import reorderAfterDrag from 'scoreboard/components/reorder-after-drag'
import icon from 'scoreboard/components/icon'
import showPartialError from 'scoreboard/datasets/show-partial-error'
import { LIST_UNIQUE_VALUES } from '../model/aggregation-type'
import { TEXT } from '../model/data-type'
import unwrapModel from '../../components/unwrap-model'

/**
 * The Choose Columns dialog (we call them aggregations in the code to make
 * things clearer).  Issue #12824
 */
var ChooseColumns = Backbone.View.extend({

	tagName: 'form',

	className: 'choose-columns',

	events: {
		'click .add': 'addColumn',
		'click .edit-button': 'changeColumn',
		'click .delete': 'deleteColumn',
		'click .reset-to-default': 'resetToDefault',
		'click .aggregation-error': 'onClickAggregationError'
	},

	/** @type {Dataset} */
	model: null,

	/**
	 * The "Choose Columns" Datasets dialog.
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {Dataset} options.model the dataset displayed on the Datasets Overview
	 *
	 * @param {Object} options.table the table whose columns are to be customized
	 *
	 * @param {Number} options.aggregationFieldId the default aggregation field ID
	 *
	 * @param {Number} options.aggregationVisibleLinkedFieldId the default
	 * aggregation visible linked field ID
	 *
	 * @param {String} options.aggregationType the default aggregation type
	 *
	 * @param {Object} options.treeItemsById a hash of tree items keyed by ID
	 *
	 * @param {String} [options.allowAppCalendar] whether "Current Calendar"
	 * should be an option when working with date filters
	 *
	 * @param {Dataset[]} options.datasets all the top-level datasets the
	 * user has permission to view
	 *
	 * @param {Boolean} [options.showDatasetLink=false] if a link to the dataset
	 * should be displayed
	 *
	 * @param {Object} options.result the current results for the table whose
	 * columns are being edited
	 */
	initialize: function(options) {
		this.options = options || {}
		var MyFormView = FormView.extend({
			test: this.test.bind(this)
		})
		this.formView = new MyFormView({
			el: this.el,
			submit: this.submit.bind(this),
			guard: false
		})
		this.aggregations = ChooseColumns.aggregations(this.model,
			this.options.table, this.options.aggregationFieldId,
			this.options.aggregationVisibleLinkedFieldId,
			this.options.aggregationType)
		this.aggregations = _.cloneDeep(this.aggregations)

		let toggleResetButton = () => {
			this.$el.toggleClass('is-showing-default-aggregations', this._isShowingDefaultAggregations())
		}

		this.formView.on('change', () => toggleResetButton())
		toggleResetButton()
	},

	render: function() {
		this.$el.empty().append(template(this.context()))
		this.renderAggregations()
		this.covertButtonTooltips = new CovertButtonTooltips({ el: this.el })
		this.formView.start()
		return this
	},

	context: function() {
		const noColumns = i18n.get('common.thereAreNo', 'report.wizard.columns')
		return {
			noColumns,
			tracked: this.model.get('tracked')
		}
	},

	/**
	 * @param {Boolean} [isDefault=false] If aggregations are being reset to
	 * default, set as `true`.
	 */
	renderAggregations: function(isDefault) {
		let el = this.$('.aggregations.panel .panel-content ul')
		el.empty()
		// important: don't do this.addAggregation.bind(this) because we don't
		// want an index passed in
		_.each(this.aggregations, (aggregation, index) => {
			this.renderColumn(aggregation, index)
			// add an error icon if appropriate
			let error = _.get(this.options.result, `aggregationErrors.${index}`)
			if (error && !isDefault) {
				el.find(`li:nth-child(${index+1}) li:first-child`).before(
					'<li><div class="aggregation-error">' +
						icon('warn') +
					'</li></div>')
			}

		})

		this.renderColumnList()
	},

	renderColumnList: function() {
		delete this.draggableList
		this.draggableList = new DraggableList(this.$('.aggregations .panel-content ul')[0])
		this.draggableList.on('move', (node, newIndex, prevIndex) => {
			reorderAfterDrag(this.aggregations, newIndex, prevIndex)
			this.formView.change()
		})
	},

	/**
	 * Renders an aggregation.  Replaces the currently render aggregation at
	 * the specified index if appropriate.
	 * @param {Object} aggregation the aggregation to render
	 * @param {Number} [index] if specified, the aggregation will be rendered
	 * at the specified index
	 */
	renderColumn: function(aggregation, index) {
		let label = aggregationLabel(this.model, aggregation)
		if (_.isEmpty(label)) {
			label = i18n.get('datasets.choose.columns.noLabel')
		}
		let html = chooseColumnsRow({
			aggregationIcon: aggregationIcon(aggregation),
			originalColumnIndex: index,
			label
		})

		let listEl = this.$('.list')
		let listItems = listEl.find('> li')
		if (_.isNil(index) || index == listItems.length) {
			listEl.append(html)
		} else {
			listItems.eq(index).replaceWith(html)
		}

		this.$('.no-columns-message').addClass('is-not-displayed')
		this.formView.validate()
	},

	addColumn: function() {
		let index = this.aggregations.length
		// create a new aggregation
		let aggregation = _.cloneDeep(_.last(this.aggregations))
		if (_.isEmpty(aggregation)) {
			aggregation = { fieldId: TOTAL, aggregationType: 'sum' }
		}
		delete aggregation.id
		delete aggregation.equation
		// don't copy any existing filters
		// https://github.com/SpiderStrategies/Scoreboard/issues/16265#issuecomment-374330476
		aggregation.filters = []
		// #25670 applying that same logic, we won't copy over the denominator
		// filters or the "useColumnFilters" setting
		aggregation.denominatorFilters = []
		aggregation.useColumnFilters = true
		// #31009 always specify a starting dataset to keep the validation in
		// data-source-form happy
		// #31753 pass in the root dataset id for child dataset views
		aggregation.startingDatasetId = this.model.get('rootDatasetId')
		// #32406 we cleared out the equation, so clear out the aggregations as
		// well
		aggregation.aggregations = []
		this.columnForm(aggregation, index)
	},

	changeColumn: function(e) {
		let index = this.$(e.target).closest('.list > li').index()
		let column = this.aggregations[index]
		this.columnForm(column, index, true)
	},

	columnForm: function(column, index, edit) {
		if (!edit) {
			// #29486 if adding a new column, display default '(equation)'
			// placeholder for the column name dropdown
			column.labelType = 'field-and-aggregation'
			delete column.label
		}
		// #59646 don't allow list unique values for edit fields for now to
		// save time. We could perhaps add support later
		let allowListUniqueValues =
			!isEditField(unwrapModel(this.options.table).fieldId)
		let form = new ColumnFormDialog({
			parentDomainType: 'dataset',
			// https://github.com/SpiderStrategies/Scoreboard/issues/45540#issuecomment-1050099534
			useStartingDataset: true,
			model: this.model,
			datasets: this.options.datasets,
			table: this.options.table,
			treeItemsById: this.options.treeItemsById,
			dataSource: column,
			allowAppCalendar: this.options.allowAppCalendar,
			allowListUniqueValues,
			includeEditFields: false, // #59625
			edit
		})
		appDisplay.dialog(form)
		form.on('done', (revisedAggregation) => {
			this.aggregations[index] = revisedAggregation
			this.renderColumn(revisedAggregation, index)
			this.formView.change()
			this.renderColumnList()
		})
	},

	deleteColumn: function(e) {
		let listItem = this.$(e.target).closest('.list > li')
		let index = listItem.index()
		this.aggregations.splice(index, 1)
		listItem.remove()
		this.$('.no-columns-message').toggleClass('is-not-displayed', this.aggregations.length > 0)
		this.formView.change()
	},

	onClickAggregationError: function(e) {
		let index = this.$(e.target).closest('li[data-original-column-index]')
			.attr('data-original-column-index')
		let err = _.get(this.options.result, `aggregationErrors.${index}`)
		showPartialError(err)
	},

	test: function() {
		var msg = FormView.prototype.test.apply(this, arguments)
		if (msg) {
			return msg
		}

		return
	},

	// Reset's the table to reflect the default aggregations
	resetToDefault: function() {
		this.aggregations = this._getDefaultAggregations()
		this.renderAggregations(true)
		this.formView.change()
	},

	submit: function() {
		let aggregations = this.aggregations
		if (this._isShowingDefaultAggregations()) {
			// #17280 a lack of aggregations is how we specify that the defaults
			// should be used.  Note the defaults change along with the Total
			// Display in the upper right corner of the Datasets Overview, so it's
			// important not to be locked into a particular set of aggregations
			aggregations = []
		}
		this.trigger('done', { aggregations })
	},

	// returns the columns rendered in the display, if there are any
	getAggregations: function() {
		var aggregations = []
		var fieldIds = this.$('[name=fieldId]')
		var aggregationTypes = this.$('[name=aggregationType]')
		aggregations = _.map(fieldIds, (fieldIdEl, index) => {
			return {
				fieldId: fieldIds.eq(index).val(),
				aggregationType: aggregationTypes.eq(index).val()
			}
		})
		return aggregations
	},

	_isShowingDefaultAggregations: function() {
		return _.isEqual(this.aggregations, this._getDefaultAggregations())
	},

	/**
	 * Returns the default aggregations for the current model and passed in options
	 */
	_getDefaultAggregations: function() {
		return _.cloneDeep(ChooseColumns.getDefaultAggregations(this.model,
			this.options.aggregationFieldId, this.options.aggregationVisibleLinkedFieldId,
			this.options.aggregationType))
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.covertButtonTooltips], 'remove')
	}

}, {

	/**
	 * Returns the aggregations in the given table, or if there are none there,
	 * the default aggregations.
	 */
	aggregations: function(dataset, table, aggregationFieldId,
			aggregationVisibleLinkedFieldId, aggregationType) {
		table = table.attributes || table
		if (_.isEmpty(table.aggregations)) {
			return this.getDefaultAggregations(dataset, aggregationFieldId,
				aggregationVisibleLinkedFieldId, aggregationType)
		}
		return table.aggregations
	},

	/**
	 * Returns the default aggregations for the aggregation field and type
	 */
	getDefaultAggregations: function(dataset, aggregationFieldId,
			aggregationVisibleLinkedFieldId, aggregationType) {
		let aggregationField = dataset.combinedFieldsById[aggregationFieldId]
		// #21677 don't use isTotal function here to support linked datasets
		// correctly
		if (aggregationFieldId == null || aggregationFieldId == TOTAL ||
			_.get(aggregationField, 'aggregationType') === 'none') {
			return [ {
				startingDatasetId: dataset.id,
				fieldId: TOTAL,
				visibleLinkedFieldId: aggregationVisibleLinkedFieldId,
				aggregationType: 'sum'
			}, {
				startingDatasetId: dataset.id,
				fieldId: TOTAL,
				visibleLinkedFieldId: aggregationVisibleLinkedFieldId,
				aggregationType: 'percentage'
			} ]
		} else {
			var amount = {
				startingDatasetId: dataset.id,
				fieldId: aggregationFieldId,
				visibleLinkedFieldId: aggregationVisibleLinkedFieldId,
				aggregationType
			}
			if (aggregationType == 'sum') {
				var percentage = {
					startingDatasetId: dataset.id,
					fieldId: aggregationFieldId,
					aggregationType: 'percentage'
				}
				return [amount, percentage]
			} else {
				return [amount]
			}
		}
	}
})

function aggregationIcon(aggregation) {
	let { fieldId, aggregationType } = aggregation
	if (fieldId == TOTAL && aggregationType === 'sum') {
		return 'number'
	}
	if (aggregationType === 'percentage') {
		return 'percent'
	}
	if (aggregationType === 'none') {
		return 'not-allowed'
	}
	if (aggregationType === 'unique') {
		return 'tally'
	}
	if (aggregationType === 'use-equation') {
		return 'calculated'
	}
	if (aggregationType === LIST_UNIQUE_VALUES) {
		return TEXT
	}
	return aggregationType
}

export default ChooseColumns
export { TOTAL }

ChooseColumns.__filename__ = 'datasets/overview/choose-columns.js'
